import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import Loader from "components/DesignSystem/Loader/loader";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { DeleteOpenItem } from "components/Transaction/RequestInfo";
import { AnimatePresence, motion } from "framer-motion";
import { useWindowSize } from "hooks/useWindowSize";
import { FC, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetOpenItemsQuery } from "store/apis/openItem";
import { useChannelStateContext } from "stream-chat-react";
import { OpenItem } from "types/Models/openItem";
import { pluralize } from "utils/pluralize";
import { MobileOpenItemList } from "./MobileOpenItemList";
import { OpenItemClarificationInput } from "./OpenItemClarificationInput";
import { OpenItemInvoiceInput } from "./OpenItemInvoiceInput";

const defaultArray: any[] = [];

const matchesSearch = (item: OpenItem, query: string) => {
  const q = query.toLowerCase();
  return (
    item.description?.toLowerCase().includes(q) ||
    item.bank_name?.toLowerCase().includes(q) ||
    item.merchant?.toLowerCase().includes(q) ||
    item.information_request?.toLowerCase().includes(q) ||
    item.amount?.toLowerCase().includes(q) ||
    item.date?.toLowerCase().includes(q)
  );
};

export const OpenItemTable: FC<{ csvId: string; addOpenItem?: boolean }> = ({
  csvId,
  addOpenItem,
}) => {
  const [search, setSearch] = useState("");
  const { isMobile } = useWindowSize();
  const { channel } = useChannelStateContext();
  const groupId = channel?.data?.group_uuid as string;

  const { data: uploadedCSV, isLoading } = useGetOpenItemsQuery(
    { csvId, groupId },
    { skip: !groupId || !csvId, refetchOnMountOrArgChange: true }
  );

  const { open_items } = uploadedCSV || {};

  const filteredItems = search
    ? (open_items || defaultArray).filter((item) => matchesSearch(item, search))
    : open_items || defaultArray;

  const columnHelper = createColumnHelper<OpenItem>();

  const clarify = addOpenItem
    ? [
        columnHelper.accessor("uuid", {
          id: "Delete",
          cell: DeleteOpenItem,
          header: "",
          size: 5,
        }),
      ]
    : [
        columnHelper.accessor("invoice", {
          id: "invoice",
          header: "Invoice",
          size: 8,
          cell: (info) => (
            <OpenItemInvoiceInput openItem={info.row.original} variant="icon" />
          ),
        }),
        columnHelper.accessor("clarification", {
          id: "clarification",
          header: "Clarification",
          size: 18,
          cell: (info) => (
            <OpenItemClarificationInput openItem={info.row.original} />
          ),
        }),
      ];

  const columns = [
    columnHelper.accessor("date", {
      id: "date",
      header: "Date",
      size: addOpenItem ? 12 : 10,
      cell: (info) => info.getValue(),
    }),

    columnHelper.accessor("merchant", {
      id: "vendor",
      header: "Vendor",
      size: addOpenItem ? 15 : 12,
      cell: (info) => {
        const merchant = info.getValue();
        const logo = info.row.original.logo;
        if (!merchant) return <span className="t-text-text-30">-</span>;
        return (
          <div className="t-flex t-gap-2 t-items-center">
            <Avatar src={logo || ""} alt={merchant} />
            <div className="t-text-subtext t-text-text-60 t-break-words">
              {merchant}
            </div>
          </div>
        );
      },
    }),

    columnHelper.accessor("bank_name", {
      id: "bank_name",
      header: "Bank",
      size: addOpenItem ? 12 : 10,
      cell: (info) => (
        <div className="t-break-words">{info.getValue() || "-"}</div>
      ),
    }),

    columnHelper.accessor("description", {
      id: "description",
      header: "Description",
      size: addOpenItem ? 18 : 12,
      cell: (info) => (
        <div className="t-break-words">{info.getValue() || "-"}</div>
      ),
    }),

    columnHelper.accessor("amount", {
      id: "amount",
      header: "Amount",
      size: addOpenItem ? 12 : 10,
      cell: (info) => {
        const { amount } = info.row.original;

        if (!Boolean(info.getValue())) {
          return "-";
        }

        if (isNaN(Number(amount))) {
          return amount;
        }

        return <AmountSuperScript amount={Number(amount)} />;
      },
    }),

    columnHelper.accessor("information_request", {
      id: "information_request",
      header: "Information Request",
      size: addOpenItem ? 30 : 15,
      cell: (info) => (
        <div className="t-break-words">{info.getValue() || "-"}</div>
      ),
    }),

    ...clarify,
  ];

  const table = useReactTable({
    data: filteredItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  if (isLoading) {
    return <Loader />;
  }

  const totalCount = open_items?.length ?? 0;
  const filteredCount = filteredItems.length;
  const showMobileList = isMobile && !addOpenItem;

  const itemCountLabel = search
    ? `${filteredCount} of ${pluralize(totalCount, "item", "items")}`
    : pluralize(totalCount, "item", "items");

  const searchBar = (
    <div className="t-flex t-items-center t-justify-between">
      <Search
        placeholder="Search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="t-w-72 sm:t-w-full"
      />
      <span className="t-text-subtext t-text-text-30 sm:t-hidden">
        {itemCountLabel}
      </span>
    </div>
  );

  if (showMobileList) {
    return (
      <div className="t-flex t-flex-col t-gap-3">
        {searchBar}
        <MobileOpenItemList openItems={filteredItems} />
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {searchBar}
      <Table.Container
        sticky={true}
        className="t-overflow-y-auto t-max-h-[55vh]"
      >
        <Table.Content>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeadCell
                    key={header.id}
                    style={{ width: `${header.getSize()}%` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                ))}
              </Table.Row>
            ))}
          </Table.Head>
          <Table.Body>
            <AnimatePresence>
              {table.getRowModel().rows.map((row) => (
                <motion.tr
                  layout
                  key={row.original.uuid}
                  transition={{ duration: 0.1, ease: "easeOut" }}
                  animate={{ x: 0 }}
                  exit={{ x: -1000 }}
                  className="t-px-3 t-border-solid t-border-neutral-0 t-border-b t-border-0 t-text-body-sm"
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      style={{ width: `${cell.column.getSize()}%` }}
                      className="t-py-3 t-px-2"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </motion.tr>
              ))}
            </AnimatePresence>
          </Table.Body>
        </Table.Content>
      </Table.Container>
    </div>
  );
};
